<template>
  <div class="container application">
    <validation-observer ref="validator" v-slot="{ errors }" tag="div">
      <h2 class="mt-2">결제 정보 확인</h2>
      <div class="payment-info">
        <div class="mt-2 payment-head bold">오늘 결제</div>
        <div class="mt-1 bold">결제하기 버튼을 누르면 아래 금액이 결제됩니다.</div>
        <div>
          <span class="bold">오늘 </span>
          <span
            class="blue050 bold">{{
            formatNumber((estimatePrice.prepaidPaymentTotalFee + estimatePrice.totalFirstMonthRentFee + estimatePrice.consignmentFee))
          }}원</span>
          <span class="bold">을 결제할 방법을 선택해 주세요.</span>
        </div>
        <ul class="mt-2 price-details">
          <li v-if="Boolean(estimatePrice.prepaidPaymentTotalFee)">
            선납금 {{ estimatePrice.prepaidRate }}% : <span
              class="blue050 bold">{{ formatNumber(estimatePrice.prepaidPaymentTotalFee) }}원</span>
          </li>
          <li>첫 달 요금 : <span class="blue050 bold">{{ formatNumber(estimatePrice.totalFirstMonthRentFee) }}원</span></li>
          <li>차량 인수 배송료 : <span class="blue050 bold">{{ formatNumber(estimatePrice.consignmentFee) }}원</span> (1회)</li>
        </ul>
        <div class="mt-2">
          <div :class="{ error: hasError(errors, '결제 수단')}">
            <validator rules="required" name="결제 수단">
              <select-box
                id="initialPayBillingId"
                :option="userAllCards"
                :default-value="application.initialPayBillingId"
                :default-option="false"
                @change="initialPayBillingIdChange"
              />
            </validator>
          </div>
        </div>
      </div>
      <vs-divider class="pt-1 pb-1" />
      <div class="payment-info">
        <div class="mt-2 payment-head bold">정기 결제</div>
        <div class="mt-1 bold">
          <span class="bold">매달 요금 </span>
          <span class="bold blue050">{{ formatNumber(estimatePrice.totalMonthlyRentFee) }}</span>
          <span class="bold">결제할 카드를 선택해주세요.</span>
        </div>
        <div class="mt-2">
          <div :class="{ error: hasError(errors, '결제 수단')}">
            <validator rules="required" name="결제 수단">
              <select-box
                id="legacyBillingId"
                :option="userRecurringCards"
                :default-value="application.legacyBillingId"
                :default-option="false"
                @change="legacyBillingIdChange"
              />
            </validator>
          </div>
        </div>
        <ul class="mt-2 price-details">
          <li>정기 결제 카드는 이용 중 변경 할 수 있습니다.</li>
          <li>쏘카 앱의 [설정] > [결제 / 운전면허]에서 결제수단을 추가할 수 있습니다.</li>
        </ul>
      </div>
    </validation-observer>

    <h2 class="mt-2">미리 알아두세요</h2>

    <div class="input-item">
      <div class="input-label">중도 해지 위약금 안내</div>
      <ul class="price-details">
        <li>계약 해지 시점에 따라 중도 해지 위약금이 발생할 수 있습니다.</li>
      </ul>
      <!-- <div class="form-guide">
        계약 해지 시점에 따라 중도 해지 위약금이 발생할 수 있습니다.
      </div> -->
      <span class="form-guide bold" @click="moveToZendesk">자세히 보기</span>
    </div>

    <div class="input-item">
      <div class="input-label">정기 결제 안내</div>
      <ul class="price-details">
        <li>매월 정기 결제일은 최초 대여 시작일의 5일 전이며, 주말 및 공휴일 여부에 따라 결제일은 달라질 수 있습니다.</li>
      </ul>
    </div>

    <div v-if="!isMonthlySubscribe" class="input-item">
      <div class="input-label">연장 계약 안내</div>
      <ul class="price-details">
        <li>연장시 최초 계약의 월 이용 요금과 상이할 수 있습니다.</li>
        <li>계약하신 차량의 상태 및 계약 조건에 따라 연장 계약 체결이 불가할 수 있습니다.</li>
      </ul>
    </div>

    <div class="input-item">
      <div class="input-label">약정 주행거리 안내</div>
      <ul class="price-details">
        <li>약정 주행거리 : 1,500km*{{application.rentMonths}}개월={{ overrunDistanse }}km (1년 환산 시, 18,000km)</li>
        <li>약정 주행거리 초과 시 km 당 주행요금 : {{ formatNumber(estimatePrice.overrunFee) }}원</li>
      </ul>
    </div>

    <div class="input-item">
      <div class="input-label">추가 운전자 안내</div>
      <ul class="price-details">
        <li>추가 운전자 등록은 결제 후 가능합니다.</li>
        <li>추후 추가 운전자 등록 여부에 따라 최종 월 결제 금액은 달라질 수 있습니다.</li>
      </ul>
    </div>

    <div class="input-item">
      <div class="input-label">차량 배송료 안내</div>
      <ul class="price-details">
        <li>인수 시 배송료는 첫 달 요금 결제 시 함께 결제됩니다.</li>
        <li>반납 시 배송료는 반납 시점에 결제되며, 쏘카플랜 계약 센터에서 별도 안내 드릴 예정입니다.</li>
      </ul>
    </div>

    <div class="input-item">
      <div class="input-label">하이패스 비용 청구 안내</div>
      <ul class="price-details">
        <li>쏘카플랜 차량은 별도 하이패스 카드 장착없이 이용할 수 있습니다.</li>
        <li>이용하신 하이패스 요금은 자동 청구됩니다.</li>
        <li>쏘카플랜에서 이용내역을 청구하기 전에 임의로 결제하실 경우 중복청구 될 수 있습니다.</li>
      </ul>
    </div>

    <vs-row class="form-group submit mb-2">
      <vs-col vs-lg="6" vs-sm="6" vs-xs="12" class="bottom-button">
        <button
          type="submit"
          class="btn btn_lg btn_full btn_outline"
          @click.prevent="back"
        >
          이전
        </button>
      </vs-col>
      <vs-col vs-lg="6" vs-sm="6" vs-xs="12" class="bottom-button">
        <button
          type="submit"
          class="btn btn_lg btn_full"
          :disabled="!valid"
          @click.prevent="next"
        >
          결제하기
        </button>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import { get, isNil, find, assign, head, cloneDeep } from 'lodash';
import SelectBox from 'components/SelectBox';
import HeaderUtil from 'utils/header';
import OpenAppUtil from 'utils/openApp';
import * as amplitude from '@amplitude/analytics-browser';

export default {
  name: 'Step05',
  components: {
    SelectBox,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    estimatePrice: {
      type: Object,
      default: () => ({}),
    },
    insurancePriceTypes: {
      type: Array,
      default: () => ([]),
    },
    userAllCards: {
      type: Array,
      default: () => ([]),
    },
    userRecurringCards: {
      type: Array,
      default: () => ([]),
    },
    isMonthlySubscribe: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      application: assign({}, cloneDeep(this.data), { legacyBillingId: get(this.userRecurringCards, '[0].id') }, { initialPayBillingId: get(this.userAllCards, '[0].id') }),
    };
  },
  computed: {
    valid() {
      return this.initialPayBillingId !== null && this.legacyBillingId !== null;
    },
    userName() {
      return this.$store.state.user.name;
    },
    legacyBillingId: {
      get() {
        return find(this.userAllCards, (item) => (item.id === this.application.legacyBillingId)) || head(this.userRecurringCards);
      },
    },
    initialPayBillingId: {
      get() {
        return find(this.userRecurringCards, (item) => (item.id === this.application.initialPayBillingId)) || head(this.userAllCards);
      },
    },
    insurancePriceType() {
      return this.application.insurancePriceType;
    },
    overrunDistanse() {
      return this.formatNumber(1500 * this.application.rentMonths);
    },
  },
  created() {
    const {
      regionGroup,
      carType,
      carTypeId,
      prepaidRate,
    } = this.data;
    const args = {
      page_name: 'plan_contract_4_check_payment',
      event_label: regionGroup.id || '',
      event_category: document.title,
      page_url: document.URL,
      component_state: prepaidRate === 0 ? '' : `prepayments_${prepaidRate * 100}%`,
      component_type: carType.thumbnailTags.map(({ id }) => id).join(','),
      component_category: carType.carClassTag.id || '',
      component_id: carTypeId,
      service_type: carType.benefitTags.map(({ id }) => id).join(','),
    };
    this.$gtag.event('view', args);
    amplitude.logEvent('view', args);
  },
  methods: {
    initialPayBillingIdChange(val) {
      this.application.initialPayBillingId = get(val, 'id', null);
    },
    legacyBillingIdChange(val) {
      this.application.legacyBillingId = get(val, 'id', null);
    },
    next() {
      if (isNil(this.application.initialPayBillingId)) {
        this.application.initialPayBillingId = head(this.initialPayBillingId).id;
      }
      if (isNil(this.application.legacyBillingId)) {
        this.application.legacyBillingId = head(this.userRecurringCards).id;
      }

      const { contractId } = this.data;
      const args = {
        page_name: 'plan_car_contract',
        event_category: document.title,
        page_url: document.URL,
        component_name: 'plan_check_payment_next_button',
        reservation_id: contractId,
      };
      this.$gtag.event('click', args);
      amplitude.logEvent('click', args);

      this.$emit('next', this.application);
    },
    back() {
      this.$emit('back');
    },
    getInsurancePriceTypeLabel(name) {
      const selected = find(this.insurancePriceTypes, (item) => (item.name === name)) || {};
      return selected.label;
    },
    moveToZendesk() {
      setTimeout(() => {
      }, 0);
      if (HeaderUtil.isAndroid()) {
        OpenAppUtil.openExternal('https://socar-plan.zendesk.com/hc/ko/articles/1500005111102');
      } else {
        window.open('https://socar-plan.zendesk.com/hc/ko/articles/1500005111102', '_blank');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.input-item {
  &.mt-5 {
    margin: 50px 0 0 0;
  }
}

.price-details {
  padding: 5px 0 10px 0;
  font-size: 12px;

  & > li {
    padding: 0 0 5px 5px;
  }
}

.mod-label {
  opacity: 0.6;

  &:hover {
    color: #00B8FF;
  }
}

.payment-head {
  font-size: 18px;
}
</style>
